import { api } from './api';

export interface CategoryScore {
  category: string;
  score: number;
  label: string;
}

export interface SectionFeedback {
  name: string;
  feedback: string;
  score: number;
}

export interface SuggestionItem {
  text: string;
  category: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
}

export interface AnalysisDetails {
  categoryScores: CategoryScore[];
  keywordGaps: string[];
  quickWins: string[];
  sections: SectionFeedback[];
  suggestions: SuggestionItem[];
}

export interface DocumentAnalysisState {
  fileName?: string | null;
  storagePath?: string | null;
  score?: number | null;
  redFlags: string[];
  suggestions: string[];
  improvedText?: string | null;
  verdict?: string | null;
  reviewedAt?: string | null;
  details?: AnalysisDetails | null;
}

export interface HistoryRecord {
  id: string;
  kind: 'resume' | 'linkedin';
  fileName: string | null;
  score: number | null;
  redFlags: string[];
  suggestions: string[];
  verdict: string | null;
  details: AnalysisDetails | null;
  createdAt: string;
}

export interface CVState {
  id: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  cv: DocumentAnalysisState;
  linkedin: DocumentAnalysisState;
}

function mapState(raw: any): CVState | null {
  if (!raw) return null;
  return {
    id: raw.id,
    userId: raw.userId,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    cv: {
      fileName: raw.resumeFileName,
      storagePath: raw.resumeStoragePath,
      score: raw.resumeScore,
      redFlags: raw.resumeRedFlags ?? [],
      suggestions: raw.resumeSuggestions ?? [],
      improvedText: raw.resumeImprovedText,
      verdict: raw.resumeVerdict,
      reviewedAt: raw.resumeReviewedAt,
      details: raw.resumeAnalysisDetails ?? null,
    },
    linkedin: {
      fileName: raw.linkedinPdfFileName,
      storagePath: raw.linkedinPdfStoragePath,
      score: raw.linkedinScore,
      redFlags: raw.linkedinRedFlags ?? [],
      suggestions: raw.linkedinSuggestions ?? [],
      improvedText: raw.linkedinImprovedText,
      verdict: raw.linkedinVerdict,
      reviewedAt: raw.linkedinReviewedAt,
      details: raw.linkedinAnalysisDetails ?? null,
    },
  };
}

export const cvService = {
  getState: async () => mapState(await api.get<any | null>('/cv/state')),
  uploadResume: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return mapState(await api.postForm<any>('/cv/resume', formData));
  },
  uploadLinkedinPdf: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return mapState(await api.postForm<any>('/cv/linkedin', formData));
  },
  getHistory: async (kind?: 'resume' | 'linkedin'): Promise<HistoryRecord[]> => {
    const params = kind ? `?kind=${kind}` : '';
    return api.get<HistoryRecord[]>(`/cv/history${params}`);
  },
  chat: async (
    message: string,
    history: Array<{ role: 'user' | 'assistant'; content: string }>,
    kind: 'resume' | 'linkedin',
  ): Promise<{ content: string }> => {
    return api.post<{ content: string }>('/cv/chat', { message, history, kind });
  },
  compareDocuments: async (): Promise<{
    consistencyScore: number;
    insights: string[];
    gaps: string[];
    recommendations: string[];
  } | null> => {
    return api.post('/cv/compare', {});
  },
};
